import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useDineOutlet } from 'hooks/api/useDineOps';
import { dineOpsService } from 'api/services/dineOpsService';
import type { DineBillingPoint, DineSection, DineDiningTable, DineKitchen, DineKitchenStation, DineQuickNote } from 'api/types';
import { propertyService } from 'api/services';
import { useDefaultProperty } from 'hooks/api/useDefaultProperty';
import DineOpsTableRangeModal from 'components/modals/modules/dineops-module/DineOpsTableRangeModal';

/**
 * SETUP-001 — the floor and the kitchen.
 *
 * Four small masters behind one screen rather than four sidebar entries:
 * sections, tables, stations and quick notes are set up once, together, when an
 * outlet opens. Splitting them across the menu would put four rarely-used items
 * in front of people who need Menu Items every day.
 *
 * Cycle 2 seats guests at these tables and routes tickets to these stations, so
 * this is what has to exist before any of that works.
 *
 * DO-309: a station can be closed for the day and reopened from its row. On
 * closing, the manager decides whether its fallback station takes its dishes;
 * the fallback can be stopped or restarted while it stays closed (spec 8.3b).
 *
 * Kitchen stations (shown as Departments until 2026-10-22) are set up on Kitchens
 * (SETUP-009), in a kitchen that links them to an organisation unit. This tab runs
 * them day to day: open, closed, fallback and printing; it has no Add. A tenant
 * master, listed by the Property filter (default: the user's default property),
 * never by the header outlet. The fallback list offers the same kitchen first.
 *
 * Tables can be added as a range (T01 to T35, DineOpsTableRangeModal). Seats are not
 * asked for on Add: covers are never checked against them and tables are joined at
 * service time; Edit keeps them as an optional hint.
 */

export type SetupTab = 'tables' | 'sections' | 'stations' | 'notes';
type Tab = SetupTab;

/**
 * When `only` is set the screen is that one master, with its own title and no tab strip (sidebar entry).
 * Sections, tables and quick notes belong to the outlet chosen in the header, so their description names
 * it; kitchen stations are shared by every outlet (DO-312), so theirs says so instead.
 */
const ONLY_TITLES: Record<Tab, { title: string; description: (outlet?: string) => string }> = {
  tables: { title: 'Tables', description: (o) => `Dining tables of ${o ?? 'this outlet'} and their sections. Switch outlet in the header` },
  sections: { title: 'Sections', description: (o) => `Areas of the floor in ${o ?? 'this outlet'}. Switch outlet in the header` },
  stations: { title: 'Kitchen Stations', description: () => 'Open and close stations during service, and switch their fallback. Stations are set up on Settings → Kitchens' },
  notes: { title: 'Quick Notes', description: (o) => `Short instructions the waiter taps instead of typing, in ${o ?? 'this outlet'}` },
};

type Row = DineSection | DineDiningTable | DineKitchenStation | DineQuickNote;

/**
 * allowedActions: the role's actions for this module (main_layout, from role
 * permissions). Defaults to [] (fail-closed). 'manage' covers add, edit and delete,
 * as in the DineOps action catalogue.
 */
const DineOpsOutletSetupScreen: React.FC<{ only?: Tab; allowedActions?: string[] }> = ({ only, allowedActions = [] }) => {
  const canManage = allowedActions.includes('manage');
  const canAdd = canManage || allowedActions.includes('add') || allowedActions.includes('create');
  const canEdit = canManage || allowedActions.includes('edit') || allowedActions.includes('update');
  const canDelete = canManage || allowedActions.includes('delete');
  const [tab, setTab] = useState<Tab>(only ?? 'tables');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [includeInactive, setIncludeInactive] = useState('');
  // Kitchen stations are a tenant master: listed by the Property filter (default: the user's default
  // property; cleared = every property), never by the header outlet. Kitchen filter: 'none' = no kitchen set.
  // The property is the user's choice once made, else the default; it is never copied into state
  // after the default arrives, which would load the list twice.
  const [chosenProperty, setChosenProperty] = useState<string | null>(null);
  const [properties, setProperties] = useState<{ value: string; label: string }[]>([]);
  const [kitchenFilter, setKitchenFilter] = useState('');
  const [kitchens, setKitchens] = useState<DineKitchen[]>([]);
  const { defaultPropertyId, loading: defaultLoading } = useDefaultProperty();
  const stationProperty = chosenProperty ?? defaultPropertyId;
  useEffect(() => {
    if (tab !== 'stations') return;
    propertyService.getAll({ page: 1, pageSize: 500, includeInactive: false })
      .then((res) => setProperties((res.data ?? []).map((p: any) => ({ value: p.id, label: p.propertyName }))))
      .catch(() => setProperties([]));
  }, [tab]);

  const [sections, setSections] = useState<DineSection[]>([]);
  // Cash counters, for the section form (DO-313): a section bills at one, or the default.
  const [counters, setCounters] = useState<DineBillingPoint[]>([]);
  const [tables, setTables] = useState<DineDiningTable[]>([]);
  const [stations, setStations] = useState<DineKitchenStation[]>([]);
  const [notes, setNotes] = useState<DineQuickNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [confirmDelete, setConfirmDelete] = useState<{ row: Row; label: string } | null>(null);
  const [editing, setEditing] = useState<{ row: Row | null } | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [rangeOpen, setRangeOpen] = useState(false);

  // Station close / reopen / fallback (DO-309). Close needs its own small dialog
  // for the fallback choice; the other three are a plain confirmation.
  const [closing, setClosing] = useState<{ station: DineKitchenStation; fallbackOn: boolean } | null>(null);
  const [stationConfirm, setStationConfirm] = useState<
    { station: DineKitchenStation; action: 'reopen' | 'fallbackOn' | 'fallbackOff' } | null
  >(null);
  const [stationBusy, setStationBusy] = useState(false);

  const { outlet } = useDineOutlet();

  // A single-master screen (`only`) loads only what it shows: Kitchen Stations needs no tables,
  // sections or notes. The combined Outlet Setup screen loads every tab, for the counts.
  const needs = useCallback((t: Tab) => !only || only === t, [only]);
  const waitForDefault = needs('stations') && chosenProperty === null && defaultLoading;

  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    if (waitForDefault) return;   // the first list is the default property's, not every station
    const seq = ++loadSeq.current;   // a reply to an older request never overwrites a newer one
    setLoading(true);
    setLoadError(null);
    const params = { pageSize: 100, includeInactive: includeInactive === 'true' || undefined };
    const propertyId = stationProperty || undefined;
    const none = Promise.resolve(null);
    try {
      const [s, t, k, n, bp, kl] = await Promise.all([
        needs('sections') || needs('tables') ? dineOpsService.getSections(params) : none,
        needs('tables') ? dineOpsService.getTables(params) : none,
        needs('stations') ? dineOpsService.getStations({ ...params, propertyId }) : none,
        needs('notes') ? dineOpsService.getQuickNotes(params) : none,
        needs('sections') ? dineOpsService.getBillingPoints().catch(() => [] as DineBillingPoint[]) : none,
        needs('stations') ? dineOpsService.getKitchenLookup(propertyId).catch(() => [] as DineKitchen[]) : none,
      ]);
      if (seq !== loadSeq.current) return;
      if (bp) setCounters(bp);
      if (kl) setKitchens(kl);
      if (s) setSections(s.data);
      if (t) setTables(t.data);
      if (k) setStations(k.data);
      if (n) setNotes(n.data);
    } catch (err: any) {
      if (seq === loadSeq.current) setLoadError(err?.response?.data?.message ?? 'The outlet setup could not be loaded.');
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [includeInactive, stationProperty, needs, waitForDefault]);

  useEffect(() => { void load(); }, [load]);

  // ------------------------------------------------------------------ the rows

  const rows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const match = (text: string) => !q || text.toLowerCase().includes(q);

    if (tab === 'tables') return tables.filter((r) => match(r.tableNumber + ' ' + (r.sectionName ?? '')));
    if (tab === 'sections') return sections.filter((r) => match(r.sectionName));
    if (tab === 'stations') {
      return stations.filter((r) => match(`${r.stationCode} ${r.stationName} ${r.kitchenName ?? ''}`)
        && (!kitchenFilter || (kitchenFilter === 'none' ? !r.kitchenId : r.kitchenId === kitchenFilter)));
    }
    return notes.filter((r) => match(r.noteText));
  }, [tab, tables, sections, stations, notes, searchQuery, kitchenFilter]);

  const kitchenOptions = useMemo(
    () => kitchens.map((k) => ({ value: k.id, label: k.propertyName ? k.kitchenName : `${k.kitchenName} (shared)` })),
    [kitchens],
  );

  const sectionOptions = useMemo(
    () => [{ value: '', label: 'No section' }, ...sections.map((s) => ({ value: s.id, label: s.sectionName }))],
    [sections],
  );

  // -------------------------------------------------------------------- saving
  // Kitchen stations are not edited here: setup is on Kitchens (SETUP-009), with their kitchen.

  const openEditor = useCallback((row: Row | null) => {
    setFormError(null);
    setEditing({ row });

    if (!row) { setDraft({}); return; }

    if (tab === 'tables') {
      const t = row as DineDiningTable;
      setDraft({ tableNumber: t.tableNumber, capacity: String(t.capacity), sectionId: t.sectionId ?? '' });
    } else if (tab === 'sections') {
      const s = row as DineSection;
      setDraft({ sectionName: s.sectionName, displayOrder: String(s.displayOrder), billingPointId: s.billingPointId ?? '' });
    } else {
      const n = row as DineQuickNote;
      setDraft({ noteText: n.noteText, displayOrder: String(n.displayOrder) });
    }
  }, [tab]);

  const save = useCallback(async () => {
    if (!editing) return;
    setSaving(true);
    setFormError(null);

    try {
      const existing = editing.row as any;

      if (tab === 'tables') {
        // Seats are optional: left out, a new table gets the default and an edited one keeps its own.
        const body = {
          tableNumber: (draft.tableNumber ?? '').trim(),
          capacity: Number(draft.capacity) || undefined,
          sectionId: draft.sectionId || undefined,
        };
        if (existing) await dineOpsService.updateTable(existing.id, { ...body, isActive: existing.isActive, rowVersion: existing.rowVersion });
        else await dineOpsService.createTable(body);
      } else if (tab === 'sections') {
        const body = {
          sectionName: (draft.sectionName ?? '').trim(),
          displayOrder: Number(draft.displayOrder) || 0,
          billingPointId: draft.billingPointId || undefined,
        };
        if (existing) await dineOpsService.updateSection(existing.id, { ...body, isActive: existing.isActive, rowVersion: existing.rowVersion });
        else await dineOpsService.createSection(body);
      } else {
        const body = { noteText: (draft.noteText ?? '').trim(), displayOrder: Number(draft.displayOrder) || 0 };
        if (existing) await dineOpsService.updateQuickNote(existing.id, { ...body, isActive: existing.isActive, rowVersion: existing.rowVersion });
        else await dineOpsService.createQuickNote(body);
      }

      notificationService.success('Saved', 'Outlet setup updated.');
      setEditing(null);
      void load();
    } catch (err: any) {
      // The API refuses a renumber on an occupied table, a duplicate code, a
      // stale row. Each needs a different response from the user, so its own
      // words are shown rather than a generic failure.
      setFormError(err?.response?.data?.message ?? 'That could not be saved.');
    } finally {
      setSaving(false);
    }
  }, [editing, draft, tab, load]);

  const doDelete = useCallback(async () => {
    if (!confirmDelete) return;
    const row = confirmDelete.row as any;
    try {
      if (tab === 'tables') await dineOpsService.deleteTable(row.id, row.rowVersion);
      else if (tab === 'sections') await dineOpsService.deleteSection(row.id, row.rowVersion);
      else await dineOpsService.deleteQuickNote(row.id, row.rowVersion);

      notificationService.success('Removed', `${confirmDelete.label} deleted.`);
      setConfirmDelete(null);
      void load();
    } catch (err: any) {
      notificationService.error('Not deleted', err?.response?.data?.message ?? 'That could not be deleted.');
      setConfirmDelete(null);
    }
  }, [confirmDelete, tab, load]);

  // ------------------------------------------------------------ station state

  const closeStation = useCallback(async () => {
    if (!closing) return;
    setStationBusy(true);
    try {
      await dineOpsService.closeStation(closing.station.id, closing.fallbackOn);
      notificationService.success(
        'Station closed',
        closing.fallbackOn
          ? `${closing.station.stationName} is closed. Its dishes go to ${closing.station.fallbackStationName}.`
          : `${closing.station.stationName} is closed. Its dishes show as unavailable.`,
      );
      setClosing(null);
      void load();
    } catch (err: any) {
      notificationService.error('Not closed', err?.response?.data?.message ?? 'The station could not be closed.');
    } finally {
      setStationBusy(false);
    }
  }, [closing, load]);

  const confirmStationAction = useCallback(async () => {
    if (!stationConfirm) return;
    const { station, action } = stationConfirm;
    setStationBusy(true);
    try {
      if (action === 'reopen') {
        await dineOpsService.reopenStation(station.id);
        notificationService.success('Station reopened', `${station.stationName} is taking tickets again.`);
      } else {
        await dineOpsService.setStationFallback(station.id, action === 'fallbackOn');
        notificationService.success(
          action === 'fallbackOn' ? 'Fallback on' : 'Fallback off',
          action === 'fallbackOn'
            ? `${station.stationName} dishes now go to ${station.fallbackStationName}.`
            : `${station.stationName} dishes show as unavailable until it reopens.`,
        );
      }
      setStationConfirm(null);
      void load();
    } catch (err: any) {
      notificationService.error('Not changed', err?.response?.data?.message ?? 'That could not be changed.');
    } finally {
      setStationBusy(false);
    }
  }, [stationConfirm, load]);

  // ------------------------------------------------------------------- columns

  const columns = useMemo((): SyutGridColumn<any>[] => {
    if (tab === 'tables') {
      return [
        { id: 'tableNumber', header: 'Table', accessorKey: 'tableNumber', enableSorting: true, size: 110 },
        { id: 'sectionName', header: 'Section', accessorKey: 'sectionName', enableSorting: true, size: 150 },
        {
          id: 'state',
          header: 'Now',
          accessorKey: 'state',
          enableSorting: true,
          size: 200,
          // Derived from the open order, not stored. A busy table cannot be
          // renumbered or removed, and this is where that becomes visible.
          cell: ({ row }) => {
            const t = row.original as DineDiningTable;
            if (t.state === 'FREE') return <span style={{ opacity: 0.6 }}>free</span>;
            return (
              <span>
                <strong>{t.state}</strong>
                {t.covers ? ` · ${t.covers} covers` : ''}
                {t.seatedMinutes != null ? ` · ${t.seatedMinutes} min` : ''}
              </span>
            );
          },
        },
        { id: 'isActive', header: 'Status', accessorKey: 'isActive', enableSorting: true, size: 100,
          cell: ({ row }) => (row.original.isActive ? 'Active' : 'Inactive') },
      ];
    }
    if (tab === 'sections') {
      return [
        { id: 'sectionName', header: 'Section', accessorKey: 'sectionName', enableSorting: true, size: 240 },
        { id: 'displayOrder', header: 'Order', accessorKey: 'displayOrder', enableSorting: true, size: 90 },
        { id: 'tableCount', header: 'Tables', accessorKey: 'tableCount', enableSorting: true, size: 100 },
        // Where the section's bills go (DO-313): its own counter, else the outlet default.
        { id: 'billingPointName', header: 'Billing point', accessorKey: 'billingPointName', enableSorting: true, size: 160,
          cell: ({ row }: any) => (row.original.billingPointId
            ? row.original.billingPointName
            : <span style={{ color: 'var(--text-muted)' }}>{row.original.billingPointName ?? 'default'} (default)</span>) },
        { id: 'isActive', header: 'Status', accessorKey: 'isActive', enableSorting: true, size: 100,
          cell: ({ row }) => (row.original.isActive ? 'Active' : 'Inactive') },
      ];
    }
    if (tab === 'stations') {
      return [
        { id: 'stationCode', header: 'Code', accessorKey: 'stationCode', enableSorting: true, size: 120 },
        { id: 'stationName', header: 'Kitchen Station', accessorKey: 'stationName', enableSorting: true, size: 200 },
        // The kitchen groups stations and carries their organisation unit.
        { id: 'kitchenName', header: 'Kitchen', accessorKey: 'kitchenName', enableSorting: true, size: 180,
          cell: ({ row }: any) => (row.original.kitchenName
            ? <span>{row.original.kitchenName}{row.original.kitchenUnitName && <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)' }}>{row.original.kitchenUnitName}</span>}</span>
            // Still takes tickets; only its setup waits until it is put in a kitchen.
            : <span className="badge badge-warning" title="Works as before. To change its printer or fallback, add it to a kitchen: Kitchens → Edit → Add existing station.">No kitchen</span>) },
        {
          id: 'isOpen',
          header: 'Now',
          accessorKey: 'isOpen',
          enableSorting: true,
          size: 230,
          // Open or closed today, and whether its dishes are going somewhere else.
          cell: ({ row }) => {
            const k = row.original as DineKitchenStation;
            if (k.isOpen) return <span className="badge badge-active">Open</span>;
            return (
              <span>
                <span className="badge badge-inactive">Closed</span>
                {k.fallbackOn
                  ? <span style={{ marginLeft: 6 }}>dishes to <strong>{k.fallbackStationName}</strong></span>
                  : <span style={{ marginLeft: 6, opacity: 0.7 }}>dishes unavailable</span>}
              </span>
            );
          },
        },
        { id: 'fallbackStationName', header: 'Fallback', accessorKey: 'fallbackStationName', enableSorting: true, size: 150,
          cell: ({ row }) => row.original.fallbackStationName || <span style={{ opacity: 0.6 }}>none</span> },
        // KOT printing (D-P10): through a Print Agent, or the kitchen screen and its browser Print button.
        { id: 'printing', header: 'Printing', accessorKey: 'printMode', enableSorting: true, size: 220,
          cell: ({ row }) => {
            const k = row.original as DineKitchenStation;
            if (k.printMode === 'AGENT') {
              return <span data-print-mode="AGENT">Agent — {k.primaryPrinterName ?? 'printer'}{(k.printCopies ?? 1) > 1 ? ` ×${k.printCopies}` : ''}
                {k.backupPrinterName && <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)' }}>backup {k.backupPrinterName}</span>}</span>;
            }
            if (k.primaryPrinterId) {
              return <span data-print-mode="BROWSER-FALLBACK" title={k.printModeReason ?? 'The printer cannot print now'}
                style={{ color: 'var(--warning, #d97706)' }}>Screen only — {k.primaryPrinterName ?? 'printer'} not usable
                {k.printModeReason && <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)' }}>{k.printModeReason}</span>}</span>;
            }
            return <span data-print-mode="BROWSER" style={{ opacity: 0.7 }}>Screen only{k.printerName ? ` · ${k.printerName}` : ''}</span>;
          } },
        { id: 'menuGroupCount', header: 'Menu groups', accessorKey: 'menuGroupCount', enableSorting: true, size: 120 },
        // Kitchen stations are shared (DO-312); the Outlets screen sets which outlets may send here.
        { id: 'usedBy', header: 'Used by', accessorKey: 'usedBy', enableSorting: true, size: 130,
          cell: ({ row }: any) => row.original.usedBy ?? <span style={{ color: 'var(--text-muted)' }}>no outlet</span> },
        { id: 'isActive', header: 'Status', accessorKey: 'isActive', enableSorting: true, size: 100,
          cell: ({ row }) => (row.original.isActive ? 'Active' : 'Inactive') },
      ];
    }
    return [
      { id: 'noteText', header: 'Quick note', accessorKey: 'noteText', enableSorting: true, size: 320 },
      { id: 'displayOrder', header: 'Order', accessorKey: 'displayOrder', enableSorting: true, size: 90 },
      { id: 'isActive', header: 'Status', accessorKey: 'isActive', enableSorting: true, size: 100,
        cell: ({ row }) => (row.original.isActive ? 'Active' : 'Inactive') },
    ];
  }, [tab]);

  const labelOf = useCallback((row: any): string => {
    if (tab === 'tables') return `Table ${row.tableNumber}`;
    if (tab === 'sections') return row.sectionName;
    if (tab === 'stations') return row.stationName;
    return row.noteText;
  }, [tab]);

  const getRowActions = useCallback(
    (row: any) => {
      const items: any[] = [];
      // Kitchen stations: open, close and fallback only; setup (and delete) is on Kitchens.
      if (canEdit && tab !== 'stations') items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={16} />, onClick: () => openEditor(row) });
      if (tab === 'stations' && canEdit) {
        const k = row as DineKitchenStation;
        if (k.isOpen) {
          items.push({ id: 'close', label: 'Close for now', icon: <IconSystem.x size={16} />,
            onClick: () => setClosing({ station: k, fallbackOn: !!k.fallbackStationId }) });
        } else {
          items.push({ id: 'reopen', label: 'Reopen', icon: <IconSystem.refresh size={16} />,
            onClick: () => setStationConfirm({ station: k, action: 'reopen' }) });
          if (k.fallbackStationId && !k.fallbackOn) {
            items.push({ id: 'fallbackOn', label: `Send dishes to ${k.fallbackStationName}`, icon: <IconSystem.check size={16} />,
              onClick: () => setStationConfirm({ station: k, action: 'fallbackOn' }) });
          }
          if (k.fallbackOn) {
            items.push({ id: 'fallbackOff', label: `Stop sending to ${k.fallbackStationName}`, icon: <IconSystem.x size={16} />,
              onClick: () => setStationConfirm({ station: k, action: 'fallbackOff' }) });
          }
        }
      }
      if (canDelete && tab !== 'stations') items.push({
        id: 'delete', label: 'Delete', icon: <IconSystem.trash size={16} />,
        onClick: () => setConfirmDelete({ row, label: labelOf(row) }),
        variant: 'danger' as const,
      });
      return items;
    },
    [openEditor, labelOf, tab, canEdit, canDelete],
  );

  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [];
    // Kitchen stations are added on Kitchens (SETUP-009); this screen runs them day to day.
    if (canAdd && tab !== 'stations') buttons.push({ id: 'add', icon: <IconSystem.add size={18} />, label: '+', onClick: () => openEditor(null), tooltip: 'Add', variant: 'icon' });
    if (canAdd && tab === 'tables') buttons.push({ id: 'add-range', icon: <IconSystem.grid size={18} />, label: '', onClick: () => setRangeOpen(true), tooltip: 'Add a range of tables (e.g. T01 to T35)', variant: 'icon' });
    buttons.push({ id: 'refresh', icon: <IconSystem.refresh size={18} />, label: '', onClick: () => void load(), tooltip: 'Refresh', variant: 'icon' });
    return buttons;
  }, [openEditor, load, canAdd, tab]);

  const tabs: { id: Tab; label: string; count: number }[] = [
    { id: 'tables', label: 'Tables', count: tables.length },
    { id: 'sections', label: 'Sections', count: sections.length },
    { id: 'stations', label: 'Kitchen Stations', count: stations.length },
    { id: 'notes', label: 'Quick notes', count: notes.length },
  ];

  return (
    <div className="module-master-container">
      <ModuleHeader
        title={only ? ONLY_TITLES[only].title : 'Outlet Setup'}
        description={
          only
            ? ONLY_TITLES[only].description(outlet ? `${outlet.outletName} (${outlet.outletCode})` : undefined)
            : outlet
              ? `${outlet.outletName} · ${outlet.pmsMode === 'ATTACHED' ? 'attached to ' + (outlet.propertyName ?? 'a property') : 'standalone'}`
              : 'Sections, tables, kitchen stations and quick notes'
        }
        icon={<IconSystem.settings size={18} />}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search this tab' }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, tooltip: 'Toggle filters' }}
        actions={actionButtons}
      />

      {!only && <div className="modal-tabs" style={{ padding: '0 16px' }}>
        {tabs.map((t) => (
          <button
            key={t.id}
            className={`modal-tab ${tab === t.id ? 'active' : ''}`}
            onClick={() => { setTab(t.id); setSearchQuery(''); }}
          >
            {t.label} <span style={{ opacity: 0.6 }}>({t.count})</span>
          </button>
        ))}
      </div>}

      <div className="module-content">
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden', minHeight: 0 }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            <SyutGrid
              data={rows as any[]}
              columns={columns}
              enableSorting
              enablePagination
              defaultPageSize={25}
              pageSizeOptions={[10, 25, 50, 100]}
              frozenRightColumns={['actions']}
              isLoading={loading}
              rowActions={getRowActions}
              emptyMessage={loadError ?? 'Nothing here yet. Use + to add one.'}
            />
          </div>

          <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)}>
                <IconSystem.x size={16} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Include inactive"
                  options={[{ value: '', label: 'Active only' }, { value: 'true', label: 'Include inactive' }]}
                  value={includeInactive}
                  onChange={(v) => setIncludeInactive((v as string) || '')}
                  placeholder="Active only"
                  size="sm"
                  isClearable
                />
              </div>
              {tab === 'stations' && (
                <>
                  <div className="filter-section">
                    <SyutSelect
                      label="Property"
                      options={properties}
                      value={stationProperty}
                      onChange={(v) => setChosenProperty((v as string) || '')}
                      placeholder="Every property"
                      helperText="Its outlets' stations, its kitchens' and any not yet used."
                      size="sm"
                      isClearable
                    />
                  </div>
                  <div className="filter-section">
                    <SyutSelect
                      label="Kitchen"
                      options={[...kitchenOptions, { value: 'none', label: 'No kitchen set' }]}
                      value={kitchenFilter}
                      onChange={(v) => setKitchenFilter((v as string) || '')}
                      placeholder="Every kitchen"
                      size="sm"
                      isClearable
                    />
                  </div>
                </>
              )}
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => { setIncludeInactive(''); setChosenProperty(''); setKitchenFilter(''); }}>Clear</button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
            </div>
          </div>
        </div>
      </div>

      {editing && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-container" style={{ minHeight: 'auto' }}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editing.row ? 'Edit' : 'Add'} — {tabs.find((t) => t.id === tab)?.label}
              </h2>
              <button className="modal-close" onClick={() => setEditing(null)} disabled={saving}>
                <IconSystem.x size={18} />
              </button>
            </div>

            <div className="modal-content">
              {formError && <div className="form-error-message" style={{ marginBottom: 12 }}>{formError}</div>}

              {tab === 'tables' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Table number <span className="text-danger">*</span></label>
                    <input className="form-input" value={draft.tableNumber ?? ''} autoFocus
                      onChange={(e) => setDraft((p) => ({ ...p, tableNumber: e.target.value }))} placeholder="e.g., T01" />
                  </div>
                  {editing.row && (
                    <div className="form-group">
                      <label className="form-label">Seats (optional)</label>
                      <input className="form-input" type="number" min={1} value={draft.capacity ?? ''}
                        onChange={(e) => setDraft((p) => ({ ...p, capacity: e.target.value }))} />
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                        A hint only. The party size is never checked against it.
                      </div>
                    </div>
                  )}
                  <div className="form-group">
                    <label className="form-label">Section</label>
                    <SyutSelect options={sectionOptions} value={draft.sectionId ?? ''} size="sm"
                      onChange={(v) => setDraft((p) => ({ ...p, sectionId: (v as string) || '' }))} placeholder="No section" />
                  </div>
                  {editing.row && (editing.row as DineDiningTable).orderId && (
                    <div style={{ fontSize: 12, color: 'var(--color-danger, #c0392b)' }}>
                      This table has an open order. Its number and section cannot change while guests
                      are seated — the kitchen is already printing that number.
                    </div>
                  )}
                </>
              )}

              {tab === 'sections' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Section name <span className="text-danger">*</span></label>
                    <input className="form-input" value={draft.sectionName ?? ''} autoFocus
                      onChange={(e) => setDraft((p) => ({ ...p, sectionName: e.target.value }))} placeholder="e.g., Terrace" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Display order</label>
                    <input className="form-input" type="number" value={draft.displayOrder ?? ''}
                      onChange={(e) => setDraft((p) => ({ ...p, displayOrder: e.target.value }))} />
                  </div>
                  <div className="form-group" data-field="billingPointId">
                    <label className="form-label">Billing point</label>
                    <SyutSelect
                      options={counters.filter((c) => c.isActive && !c.isDefault).map((c) => ({ value: c.id, label: c.billingPointName }))}
                      value={draft.billingPointId ?? ''} size="sm" isClearable menuPortalTarget={document.body}
                      onChange={(v) => setDraft((p) => ({ ...p, billingPointId: (v as string) || '' }))}
                      placeholder={`Default (${counters.find((c) => c.isDefault)?.billingPointName ?? 'outlet default'})`} />
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                      Bills from this section&rsquo;s tables go to this counter. Leave it on the default to use the outlet&rsquo;s default counter.
                    </div>
                  </div>
                </>
              )}

              {tab === 'notes' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Note <span className="text-danger">*</span></label>
                    <input className="form-input" value={draft.noteText ?? ''} autoFocus
                      onChange={(e) => setDraft((p) => ({ ...p, noteText: e.target.value }))} placeholder="e.g., No onion" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Display order</label>
                    <input className="form-input" type="number" value={draft.displayOrder ?? ''}
                      onChange={(e) => setDraft((p) => ({ ...p, displayOrder: e.target.value }))} />
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={() => void save()} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </>
      )}

      <DineOpsTableRangeModal
        isOpen={rangeOpen}
        existingNumbers={tables.map((t) => t.tableNumber)}
        sectionOptions={sectionOptions}
        onSaved={() => { setRangeOpen(false); void load(); }}
        onClose={() => setRangeOpen(false)}
      />

      {closing && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-container" style={{ minHeight: 'auto', maxWidth: 480 }}>
            <div className="modal-header" style={{ minHeight: 'auto' }}>
              <h2 className="modal-title">Close {closing.station.stationName}?</h2>
              <button className="modal-close" onClick={() => setClosing(null)} disabled={stationBusy}>
                <IconSystem.x size={18} />
              </button>
            </div>
            <div className="modal-content">
              <p style={{ marginTop: 0 }}>
                No ticket will go to {closing.station.stationName} until it is reopened. Tickets already
                sent stay where they are.
              </p>
              {closing.station.fallbackStationId ? (
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <input type="checkbox" checked={closing.fallbackOn}
                    onChange={(e) => setClosing((p) => p && ({ ...p, fallbackOn: e.target.checked }))} />
                  <span>
                    Send its dishes to <strong>{closing.station.fallbackStationName}</strong> while it is closed.
                    <div style={{ fontSize: 12, opacity: 0.7 }}>
                      Check with {closing.station.fallbackStationName} first. Dishes that opt out of fallback stay unavailable.
                    </div>
                  </span>
                </label>
              ) : (
                <div style={{ fontSize: 13, opacity: 0.8 }}>
                  It has no fallback station, so its dishes will show as unavailable. Set a fallback on Setup &rsaquo; Restaurant &rsaquo; Kitchens to change that.
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ minHeight: 'auto' }}>
              <button className="btn btn-secondary" onClick={() => setClosing(null)} disabled={stationBusy}>Cancel</button>
              <button className="btn btn-danger" onClick={() => void closeStation()} disabled={stationBusy}>
                {stationBusy ? 'Closing…' : 'Close station'}
              </button>
            </div>
          </div>
        </>
      )}

      {stationConfirm && (
        <ConfirmDialog
          open
          title={
            stationConfirm.action === 'reopen' ? `Reopen ${stationConfirm.station.stationName}?`
              : stationConfirm.action === 'fallbackOn' ? `Send dishes to ${stationConfirm.station.fallbackStationName}?`
              : `Stop sending to ${stationConfirm.station.fallbackStationName}?`
          }
          message={
            stationConfirm.action === 'reopen'
              ? 'New tickets go to it again. Its fallback, if on, stops. Tickets already at the fallback stay there.'
              : stationConfirm.action === 'fallbackOn'
                ? `${stationConfirm.station.stationName} dishes will be sent to ${stationConfirm.station.fallbackStationName}, marked as a fallback. Check with that kitchen first.`
                : `${stationConfirm.station.stationName} dishes will show as unavailable until it reopens. Tickets already sent stay where they are.`
          }
          confirmLabel={stationConfirm.action === 'reopen' ? 'Reopen' : stationConfirm.action === 'fallbackOn' ? 'Send dishes' : 'Stop'}
          cancelLabel="Cancel"
          variant={stationConfirm.action === 'fallbackOff' ? 'danger' : 'default'}
          loading={stationBusy}
          loadingMessage="Saving…"
          onConfirm={() => void confirmStationAction()}
          onCancel={() => setStationConfirm(null)}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          open
          title={`Delete ${confirmDelete.label}?`}
          message="Anything still depending on it will block the delete, and you will be told what."
          confirmLabel="Delete"
          cancelLabel="Keep it"
          variant="danger"
          onConfirm={doDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};

export default DineOpsOutletSetupScreen;
